import moment from 'moment';

export const buildOrderPayload = (formValues) => {
  const {
    planDetails,
    firstName,
    lastName,
    email,
    phone,
    pin,
    secretQuestion,
    secretQuestionAnswer,
    socialSecurityNumber,
    dateOfBirth,
    installationDate,
    address,
    paymentDetails,
  } = formValues;

  const parsedInternetPrice = planDetails.price ? Number(planDetails.price).toFixed(2) : null;
  const parsedInternetSpeed = planDetails.downloadSpeed
    ? parseInt(planDetails.downloadSpeed, 10)
    : null;

  const orderPayload = {
    provider: 'att',
    action: 'place-order',
    params: {
      planSelection: {
        price: parsedInternetPrice,
        downloadSpeed: parsedInternetSpeed,
      },
      account: {
        firstName,
        lastName,
        email,
        phone,
        pin,
        secretQuestion,
        secretQuestionAnswer,
        socialSecurityNumber,
        dateOfBirth: new Date(dateOfBirth).toISOString(),
      },
      address: {
        line1: address.line1,
        city: address.city,
        state: address.state,
        zipCode: address.zipCode,
      },
      paymentDetails: {
        firstName: paymentDetails.firstName,
        lastName: paymentDetails.lastName,
        cardType: paymentDetails.cardType,
        cardNumber: paymentDetails.cardNumber,
        expirationMonth: paymentDetails.expirationMonth,
        expirationYear: paymentDetails.expirationYear,
        cvv: paymentDetails.cvv,
        billingAddress: {
          line1: paymentDetails.billingAddress.line1,
          city: paymentDetails.billingAddress.city,
          state: paymentDetails.billingAddress.state,
          zipCode: paymentDetails.billingAddress.zipCode,
        },
      },
      installationDate: new Date(installationDate).toISOString(),
    },
  };
  return orderPayload;
};

export const handleOnSelectChange = ({ setFieldValue, selectedActivation }) => {
  const dateOfBirth = selectedActivation?.providerAnswers?.dob
    ? moment(selectedActivation.providerAnswers.dob).format('MM/DD/YYYY')
    : null;

  setFieldValue('utilityActivationItemId', selectedActivation.itemId);
  setFieldValue(
    'planDetails.price',
    selectedActivation.variantPrice || selectedActivation.planPrice,
  );
  setFieldValue('planDetails.downloadSpeed', selectedActivation.planInternetSpeed);
  setFieldValue('firstName', selectedActivation.userFirstName);
  setFieldValue('lastName', selectedActivation.userLastName);
  setFieldValue('email', selectedActivation.userEmail);
  setFieldValue('phone', selectedActivation.userPhone);
  setFieldValue('pin', selectedActivation.providerAnswers?.pin || '');
  setFieldValue('secretQuestion', selectedActivation.providerAnswers?.securityQuestion || '');
  setFieldValue(
    'secretQuestionAnswer',
    selectedActivation.providerAnswers?.securityQuestionAnswer || '',
  );
  setFieldValue('socialSecurityNumber', selectedActivation.providerAnswers?.ssn || '');
  setFieldValue('dateOfBirth', dateOfBirth || '');
  setFieldValue('installationDate', selectedActivation.leaseStartDate || '');
  setFieldValue('address.line1', selectedActivation.street || '');
  setFieldValue('address.city', selectedActivation.city);
  setFieldValue('address.state', selectedActivation.state);
  setFieldValue('address.zipCode', selectedActivation.zip);
  setFieldValue('paymentDetails.firstName', selectedActivation.purchaseDetails?.ccFirstName || '');
  setFieldValue('paymentDetails.lastName', selectedActivation.purchaseDetails?.ccLastName || '');
  setFieldValue('paymentDetails.cardType', selectedActivation.purchaseDetails?.ccNetwork || '');
  setFieldValue('paymentDetails.cardNumber', selectedActivation.purchaseDetails?.ccNumber || '');
  setFieldValue(
    'paymentDetails.expirationMonth',
    selectedActivation.purchaseDetails?.ccExpMonth || '',
  );
  setFieldValue(
    'paymentDetails.expirationYear',
    selectedActivation.purchaseDetails?.ccExpYear || '',
  );
  setFieldValue('paymentDetails.cvv', selectedActivation.purchaseDetails?.ccCvc || '');
  setFieldValue(
    'paymentDetails.billingAddress.line1',
    selectedActivation.purchaseDetails?.addressLine1 || '',
  );
  setFieldValue(
    'paymentDetails.billingAddress.city',
    selectedActivation.purchaseDetails?.addressCity || '',
  );
  setFieldValue(
    'paymentDetails.billingAddress.state',
    selectedActivation.purchaseDetails?.addressState || '',
  );
  setFieldValue(
    'paymentDetails.billingAddress.zipCode',
    selectedActivation.purchaseDetails?.zipCode || '',
  );
};

export const getFormFields = ({ options, handleSelectChange }) => [
  {
    id: 'order',
    type: 'select',
    title: 'Activation',
    properties: {
      choices: options,
      onChange: (e, formikBag) => handleSelectChange(e, formikBag.setFieldValue),
    },
  },
  {
    id: 'planDetails.price',
    type: 'short_text',
    title: 'Plan Price',
    validations: ['required'],
  },
  {
    id: 'planDetails.downloadSpeed',
    type: 'short_text',
    title: 'Download Speed',
    validations: ['required'],
  },
  {
    id: 'firstName',
    type: 'short_text',
    title: 'First Name',
    validations: ['required'],
  },
  {
    id: 'lastName',
    type: 'short_text',
    title: 'Last Name',
    validations: ['required'],
  },
  {
    id: 'email',
    type: 'short_text',
    title: 'Email',
    validations: ['email', 'required'],
  },
  {
    id: 'phone',
    type: 'phone',
    title: 'Phone',
    validations: ['phone'],
  },
  {
    id: 'pin',
    type: 'short_text',
    title: 'PIN',
    properties: {
      dataDogMasked: true,
    },
    validations: ['required'],
  },
  {
    id: 'secretQuestion',
    type: 'short_text',
    title: 'Secret Question',
    properties: {
      dataDogMasked: true,
    },
    validations: ['required'],
  },
  {
    id: 'secretQuestionAnswer',
    type: 'short_text',
    title: 'Secret Answer',
    properties: {
      dataDogMasked: true,
    },
    validations: ['required'],
  },
  {
    id: 'socialSecurityNumber',
    type: 'short_text',
    title: 'SSN',
    properties: {
      dataDogMasked: true,
    },
    validations: ['required'],
  },
  {
    id: 'dateOfBirth',
    title: 'Date of birth',
    type: 'date_picker',
    properties: {
      dateFormat: 'MM/DD/YYYY',
      dataDogMasked: true,
    },
    validations: ['required', 'date'],
  },
  {
    id: 'installationDate',
    title: 'Installation Date',
    type: 'date_picker',
    properties: {
      dateFormat: 'MM/DD/YYYY',
    },
    validations: ['required', 'date'],
  },
  {
    id: 'address.line1',
    type: 'short_text',
    title: 'Address Line 1',
    validations: ['required'],
  },
  {
    id: 'address.city',
    type: 'short_text',
    title: 'City',
    validations: ['required'],
  },
  {
    id: 'address.state',
    type: 'short_text',
    title: 'State',
    validations: ['required', 'stateCode'],
  },
  {
    id: 'address.zipCode',
    type: 'short_text',
    title: 'Zip Code',
    validations: ['required', 'zipCode'],
  },
  {
    id: 'paymentDetails.firstName',
    type: 'short_text',
    title: 'First Name on Card',
    properties: {
      dataDogMasked: true,
    },
    validations: ['required'],
  },
  {
    id: 'paymentDetails.lastName',
    type: 'short_text',
    title: 'Last Name on Card',
    properties: {
      dataDogMasked: true,
    },
    validations: ['required'],
  },
  {
    id: 'paymentDetails.cardType',
    type: 'select',
    title: 'Card Type',
    properties: {
      choices: [
        { value: 'Visa', label: 'Visa' },
        { value: 'MasterCard', label: 'MasterCard' },
        { value: 'American Express', label: 'American Express' },
        { value: 'Discover', label: 'Discover' },
      ],
      dataDogMasked: true,
    },
    validations: ['required'],
  },
  {
    id: 'paymentDetails.cardNumber',
    type: 'short_text',
    title: 'Card Number',
    properties: {
      dataDogMasked: true,
    },
    validations: ['required'],
  },
  {
    id: 'paymentDetails.expirationMonth',
    type: 'short_text',
    title: 'Expiration Month',
    properties: {
      dataDogMasked: true,
    },
    validations: ['required'],
  },
  {
    id: 'paymentDetails.expirationYear',
    type: 'short_text',
    title: 'Expiration Year',
    properties: {
      dataDogMasked: true,
    },
    validations: ['required'],
  },
  {
    id: 'paymentDetails.cvv',
    type: 'short_text',
    title: 'CVV',
    properties: {
      dataDogMasked: true,
    },
    validations: ['required'],
  },
  {
    id: 'paymentDetails.billingAddress.line1',
    type: 'short_text',
    title: 'Address Line 1',
    validations: ['required'],
  },
  {
    id: 'paymentDetails.billingAddress.city',
    type: 'short_text',
    title: 'City',
    validations: ['required'],
  },
  {
    id: 'paymentDetails.billingAddress.state',
    type: 'short_text',
    title: 'State',
    validations: ['required', 'stateCode'],
  },
  {
    id: 'paymentDetails.billingAddress.zipCode',
    type: 'short_text',
    title: 'Zip Code',
    validations: ['required', 'zipCode'],
  },
  {
    id: 'utilityActivationItemId',
    type: 'hidden',
  },
];
